import React, { memo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ChartsArea = memo(({ stats, loading, scoreType, scoreTypeDisplayName }) => {
    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Updating charts...</p>
                </div>
            </div>
        );
    }

    if (!stats) return null;

    const chartConfigs = [
        {
            title: `📈 ${scoreTypeDisplayName} Distribution`,
            data: stats['data'],
            description: `Distribution of sentences by ${scoreTypeDisplayName.toLowerCase()} ranges over time`,
            type: 'stacked',
            importance: 'high'
        },
        {
            title: `📊 ${scoreTypeDisplayName} Normalized Distribution`,
            data: stats['normdata'],
            description: `Percentage distribution across ${scoreTypeDisplayName.toLowerCase()} ranges`,
            type: 'stacked',
            importance: 'high'
        },
        {
            title: `🎯 Average ${scoreTypeDisplayName} - All Attempts`,
            data: stats['average'],
            yDomain: [70, 100],
            description: `Average ${scoreTypeDisplayName.toLowerCase()} across all attempts`,
            type: 'single',
            importance: 'medium'
        },
        {
            title: `🚀 Average ${scoreTypeDisplayName} - First Attempts`,
            data: stats['first'],
            yDomain: [70, 100],
            description: `Average ${scoreTypeDisplayName.toLowerCase()} for first attempts only`,
            type: 'single',
            importance: 'medium'
        },
        {
            title: `⭐ Average ${scoreTypeDisplayName} - Best Attempts`,
            data: stats['best'],
            yDomain: [70, 100],
            description: `Average ${scoreTypeDisplayName.toLowerCase()} for best attempts`,
            type: 'single',
            importance: 'medium'
        }
    ];

    // Custom tooltip formatter
    const customTooltipFormatter = (value, name) => {
        const nameMap = {
            'green': '80-100% (Excellent)',
            'yellow': '40-80% (Good)', 
            'red': '0-40% (Needs Practice)',
            'average': `${scoreTypeDisplayName} Score`
        };
        return [value, nameMap[name] || name];
    };

    return (
        <div className="space-y-6">
            {/* Top Row - High Importance Charts */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {chartConfigs
                    .filter(config => config.importance === 'high')
                    .map((config, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300 hover:border-blue-300">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-800">{config.title}</h3>
                                <p className="text-gray-600 text-sm mt-1">{config.description}</p>
                            </div>
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 text-sm font-bold">{index + 1}</span>
                            </div>
                        </div>
                        
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={config.data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fontSize: 12 }}
                                        axisLine={false}
                                    />
                                    <YAxis 
                                        domain={config.yDomain || [0, 'auto']}
                                        tick={{ fontSize: 12 }}
                                        axisLine={false}
                                        label={{ 
                                            value: scoreTypeDisplayName, 
                                            angle: -90, 
                                            position: 'insideLeft',
                                            offset: -5,
                                            style: { fontSize: 12 }
                                        }}
                                    />
                                    <Tooltip 
                                        formatter={customTooltipFormatter}
                                        contentStyle={{
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                    <Legend 
                                        wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                                        formatter={(value) => customTooltipFormatter(null, value)[1]}
                                    />
                                    
                                    {config.type === 'stacked' ? (
                                        <>
                                            <Bar 
                                                name="green" 
                                                dataKey="green" 
                                                stackId="a" 
                                                fill="#10B981" 
                                                radius={[2, 2, 0, 0]}
                                            />
                                            <Bar 
                                                name="yellow" 
                                                dataKey="yellow" 
                                                stackId="a" 
                                                fill="#F59E0B" 
                                                radius={[2, 2, 0, 0]}
                                            />
                                            <Bar 
                                                name="red" 
                                                dataKey="red" 
                                                stackId="a" 
                                                fill="#EF4444" 
                                                radius={[2, 2, 0, 0]}
                                            />
                                        </>
                                    ) : (
                                        <Bar 
                                            name="average" 
                                            dataKey="average" 
                                            fill="#3B82F6" 
                                            radius={[2, 2, 0, 0]}
                                        />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ))}
            </div>

            {/* Bottom Row - Medium Importance Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {chartConfigs
                    .filter(config => config.importance === 'medium')
                    .map((config, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-md border border-gray-200 p-4 hover:shadow-lg transition-all duration-300 hover:border-blue-200 group">
                        <div className="mb-3">
                            <h3 className="text-lg font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">
                                {config.title}
                            </h3>
                            <p className="text-gray-600 text-xs mt-1">{config.description}</p>
                        </div>
                        
                        <div className="h-48">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={config.data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                    <XAxis 
                                        dataKey="date" 
                                        tick={{ fontSize: 10 }}
                                        axisLine={false}
                                    />
                                    <YAxis 
                                        domain={config.yDomain}
                                        tick={{ fontSize: 10 }}
                                        axisLine={false}
                                        label={{ 
                                            value: scoreTypeDisplayName, 
                                            angle: -90, 
                                            position: 'insideLeft',
                                            offset: -5,
                                            style: { fontSize: 10 }
                                        }}
                                    />
                                    <Tooltip formatter={(value) => [value, scoreTypeDisplayName]} />
                                    <Bar 
                                        name="average" 
                                        dataKey="average" 
                                        fill="#3B82F6" 
                                        radius={[2, 2, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ))}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">{stats.data?.length || 0}</div>
                    <div className="text-blue-100 text-sm">Time Periods</div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">
                        {stats.data?.reduce((sum, item) => sum + (item.green || 0), 0) || 0}
                    </div>
                    <div className="text-green-100 text-sm">Excellent Scores</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">
                        {stats.data?.reduce((sum, item) => sum + (item.yellow || 0), 0) || 0}
                    </div>
                    <div className="text-yellow-100 text-sm">Good Scores</div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">
                        {stats.data?.reduce((sum, item) => sum + (item.red || 0), 0) || 0}
                    </div>
                    <div className="text-red-100 text-sm">Needs Practice</div>
                </div>
            </div>
        </div>
    );
});

ChartsArea.displayName = 'ChartsArea';

export default ChartsArea;